import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from '@/hooks/use-auth';
import { ticketRulesService } from '@/services/ticket-rules.service';
import type { RunsFilter, RunsPage } from '@/types/rules.types';
import { TICKET_RULES_KEY } from './use-ticket-rules';

const runsQueryAtom = (filter: RunsFilter) =>
	atomWithInfiniteQuery((get) => ({
		queryKey: [TICKET_RULES_KEY, 'runs', buScopeKey(get), filter],
		queryFn: ({ pageParam }: { pageParam: string | undefined }) => ticketRulesService.runs(filter, pageParam, buScopeParams(get)),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage: RunsPage) => lastPage.nextCursor ?? undefined,
		enabled: get(isAuthenticatedAtom)
	}));

/** The brand's Activity log, newest first, a page at a time. `filter` must be memoised. */
export function useTicketRuleRuns(filter: RunsFilter) {
	const queryAtom = useMemo(() => runsQueryAtom(filter), [filter]);
	const { data, isLoading, isError, isFetchNextPageError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } = useAtomValue(queryAtom);
	const runs = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	return {
		runs,
		isLoading,
		isError,
		loadMoreFailed: isFetchNextPageError,
		refetch,
		hasMore: Boolean(hasNextPage),
		loadingMore: isFetchingNextPage,
		loadMore
	};
}

const runQueryAtom = (id: string) =>
	atomWithQuery((get) => ({
		queryKey: [TICKET_RULES_KEY, 'run', buScopeKey(get), id],
		queryFn: () => ticketRulesService.run(id, buScopeParams(get)),
		enabled: get(isAuthenticatedAtom) && Boolean(id)
	}));

/** One run with every step; pass an empty id for none. */
export function useTicketRuleRun(id: string) {
	const atom = useMemo(() => runQueryAtom(id), [id]);
	return useAtomValue(atom);
}
